'use client';

import { useId } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import { classnames } from '@/utils/classnames/classnames';
import Link from '@/components/atoms/link';
import useCustomRouter from '@/hooks/useCustomRouter';
import { CurrencyHelpers } from '@/utils/currency-helpers';
import { TruckIcon } from '@heroicons/react/24/outline';
import useShipEstimate from '@/lib/hooks/useShipEstimate';
import DiscountsForm from '@/components/molecules/discounts-form';
import { splitDiscountForDisplay } from '@/lib/utils/display-discount';
import { useFlushPendingCartWrites } from '../cart/cart-flush-registry';
import FisProgressBar from './components/fis-progress-bar';
import type { CartOrderSummaryProps } from './types';

// EWM-414 — relative-word → i18n key map. Explicit literals (no template
// interpolation) so the orphaned-i18n CI check finds every key.
// EWM-939: the 'today' entry is back — NZ dispatches same-day below its 10:55
// Auckland cutoff. AU never sends the token, but this map must be total over
// the union or a live NZ cart renders a blank ship line.
const SHIP_RELATIVE_KEY = {
  today: 'cart.ship-today',
  tomorrow: 'cart.ship-tomorrow',
  'next-business-day': 'cart.ship-next-business-day',
} as const;

// EWM-332 — the Shipping line stays OFF the cart rail (freight is "apply at pay",
// computed + shown on checkout only).
//
// EWM-204 / EWM-332 reversal — discount-code ENTRY lives on the cart again. It was
// briefly restricted to checkout as a rider on the EWM-332 freight rework, but that
// was a UX choice, not a constraint: CT applies a redeemed code to the cart
// immediately + page-agnostically. Entry + applied-code management is the shared
// `DiscountsForm`; the checkout page now shows the applied code read-only. The
// pricing breakdown below still shows the discount amount (EWM-607).
const CartOrderSummary = ({
  transaction,
  fisThreshold,
  checkoutDisabled = false,
  onCheckout,
  className,
  discountCodes,
  onApplyDiscount,
  discountReadOnly = false,
}: CartOrderSummaryProps) => {
  const translate = useTranslations();
  const params = useParams();
  const router = useCustomRouter();
  const flushPendingCartWrites = useFlushPendingCartWrites();
  const sectionId = useId();
  const locale = typeof params.locale === 'string' ? params.locale : 'en-AU';

  const { subtotal, discount, total } = transaction;
  const currencyCode = total.currencyCode;
  const fractionDigits = total.fractionDigits;
  const fmt = (cents: number) => CurrencyHelpers.formatForCurrency(cents, locale, currencyCode, fractionDigits);

  // EWM-716 — a product/sale discount is shown ONCE, on the line item (its discounted
  // price). So Subtotal is the sum of the DISCOUNTED line totals, and the Discount row
  // carries ONLY cart-level discounts (a code / discount-on-total) — the amounts not
  // already baked into the line prices — so a line-level saving is never counted twice.
  // `subtotal`/`discount` from calculateTransaction stay pre-discount (the FIS base below
  // still uses the raw pre-discount subtotal).
  const { displaySubtotalCents, cartLevelDiscountCents, cartLevelSegments } = splitDiscountForDisplay(
    subtotal.centAmount,
    discount.centAmount,
    discount.segments,
  );
  // Segment labels are mixed: sale/gift segments carry i18n keys (cart.*);
  // cart-discount names + discount codes are raw merchandiser strings — render
  // those verbatim rather than through translate (which would miss the key).
  const segmentLabel = (label: string) =>
    label.startsWith('cart.') ? translate(label as Parameters<typeof translate>[0]) : label;

  // EWM-477 — one coupon at a time: once a code is applied, disable further entry
  // (the customer removes the active code to enter another). View-only users also
  // get the read-only form (visible, Apply disabled — EWM-204 AC).
  const hasActiveDiscount = (discountCodes?.length ?? 0) > 0;

  // EWM-414 — delivery ship-date estimate. FIS-met is decided HERE with the
  // SAME inputs as the "free instore" FIS progress bar (net items subtotal vs
  // the BU threshold), so the ship line and that message can never disagree;
  // the BFF composes the date from the delivery calendar + the BU pick days.
  const fisMet = fisThreshold !== undefined && fisThreshold > 0 && subtotal.centAmount >= fisThreshold;
  const { shipEstimate } = useShipEstimate(fisMet);
  const shipLine =
    shipEstimate?.basis === 'pickDay' && shipEstimate.weekday
      ? translate('cart.ship-on-weekday', { weekday: shipEstimate.weekday })
      : shipEstimate?.relative
        ? translate(SHIP_RELATIVE_KEY[shipEstimate.relative])
        : null;

  // Cart page shows the cart-items total ONLY — no GST line, no computed grand
  // total. The displayed total is the items subtotal less any applied discount.
  // Tax + freight are computed and shown on the checkout page (where the
  // shipping address is set), never here.
  const netSubtotal = subtotal.centAmount - discount.centAmount;
  // FIS (free-freight) eligibility compares the PRE-discount net items subtotal
  // against the threshold (agreed 2026-06-15) — coupons do NOT reduce eligibility,
  // and GST/freight are excluded. Matches the BFF's applyFreightToCart base.
  const fisBaseCents = subtotal.centAmount;

  const handleCheckout = async () => {
    // EWM-613 — flush any in-flight debounced quantity edits BEFORE leaving /cart, so
    // checkout reads the latest quantities (not a mid-debounce stale value). Resolves
    // immediately when nothing is pending.
    await flushPendingCartWrites();
    await onCheckout?.();
    router.push('/checkout');
  };

  return (
    <section
      className={classnames('rounded-lg border border-gray-200 bg-white p-6', className)}
      aria-labelledby={`${sectionId}-title`}
    >
      <h2 id={`${sectionId}-title`} className="text-16 mb-5 font-bold text-gray-800">
        {translate('cart.order-summary')}
      </h2>

      {fisThreshold !== undefined && fisThreshold > 0 && (
        <FisProgressBar
          subtotal={fisBaseCents}
          threshold={fisThreshold}
          currencyCode={currencyCode}
          fractionDigits={fractionDigits}
        />
      )}

      <dl className="mb-4 space-y-3">
        <div className="text-13 flex justify-between text-gray-700">
          <dt>{translate('cart.subtotal')}</dt>
          <dd>{fmt(displaySubtotalCents)}</dd>
        </div>
        {cartLevelDiscountCents > 0 && (
          <>
            <div className="text-13 flex justify-between text-gray-700">
              <dt>
                {cartLevelSegments.length === 1 && cartLevelSegments[0].source
                  ? `${translate('cart.discount')} (${cartLevelSegments[0].source})`
                  : translate('cart.discount')}
              </dt>
              <dd>-{fmt(cartLevelDiscountCents)}</dd>
            </div>
            {cartLevelSegments.length > 1 &&
              cartLevelSegments.map((segment, index) => (
                <div
                  key={`${segment.source}-${segment.label}-${index}`}
                  className="text-12 flex justify-between gap-3 pl-3 text-gray-500"
                >
                  <dt className="font-normal">
                    {segment.source ? <span className="font-semibold">{segment.source} - </span> : null}
                    {segmentLabel(segment.label)}
                  </dt>
                  <dd className="text-green-600">-{fmt(segment.discountedAmount)}</dd>
                </div>
              ))}
          </>
        )}
      </dl>

      {/* EWM-204 — discount-code entry + applied-code management (shared
          DiscountsForm). Rendered when the consumer wires apply (the cart tastic
          always does; stories may omit). Applied-code chips + Remove live inside;
          readOnly (view-only or a code already applied) disables Apply per EWM-477. */}
      {onApplyDiscount && (
        <DiscountsForm
          discounts={discountCodes ?? []}
          onSubmit={onApplyDiscount}
          readOnly={discountReadOnly || hasActiveDiscount}
        />
      )}

      <div className="mb-6 flex items-baseline justify-between">
        <span className="text-16 font-bold text-gray-800">{translate('cart.total')}</span>
        <span className="text-primary text-20 font-bold">{fmt(netSubtotal)}</span>
      </div>

      {shipLine && (
        <p className="text-13 mb-4 flex items-center gap-2 text-gray-700" data-testid="cart-ship-estimate">
          <TruckIcon className="size-4 shrink-0 text-gray-500" aria-hidden="true" />
          <span>{shipLine}</span>
        </p>
      )}

      <button
        type="button"
        disabled={checkoutDisabled}
        aria-disabled={checkoutDisabled}
        onClick={checkoutDisabled ? undefined : () => void handleCheckout()}
        className={classnames(
          'text-14 mb-3 w-full rounded-lg py-3 font-bold transition-colors',
          checkoutDisabled
            ? 'cursor-not-allowed bg-gray-300 text-gray-500'
            : 'bg-primary hover:bg-primary/90 text-white',
        )}
      >
        {translate('cart.checkout-go')}
      </button>

      <Link href="/" className="text-primary text-14 block w-full py-2 text-center font-semibold">
        {translate('cart.continue-shopping')}
      </Link>
    </section>
  );
};

export default CartOrderSummary;
